import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type PropsWithChildren,
} from "react";
import { noop } from "ts-essentials";

import type { AndOrCondition, Condition } from "src/types/visual";

export type AgentNudgeMetadata = {
  focusedConditions?: AndOrCondition<Condition>;
  focusedLabel?: string;
  autoSubmit?: boolean;
};

type NudgeListener = (prompt: string, metadata?: AgentNudgeMetadata) => void;
type LoadSessionListener = (sessionId: string) => void;
type StartNewChatListener = () => void;
type OpenListener = () => void;

export type AgentControlContextValue = {
  agentAvailable: boolean;
  /** Send a prompt to the agent pane (opens the panel if closed). */
  nudgeAgent?: NudgeListener;
  /** Open the agent panel and load a session into it. */
  loadSession?: LoadSessionListener;
  /** Open the agent panel on a fresh chat. */
  startNewChat?: StartNewChatListener;
  /** Registrations return a cleanup callback for useEffect. */
  registerNudgeListener: (listener: NudgeListener) => () => void;
  registerLoadSessionListener: (listener: LoadSessionListener) => () => void;
  registerStartNewChatListener: (listener: StartNewChatListener) => () => void;
  registerOpenListener: (listener: OpenListener) => () => void;
};

const AgentControlContext = createContext<AgentControlContextValue>({
  agentAvailable: false,
  registerNudgeListener: () => noop,
  registerLoadSessionListener: () => noop,
  registerStartNewChatListener: () => noop,
  registerOpenListener: () => noop,
});
AgentControlContext.displayName = "AgentControlContext";
export const useAgentControlContext = () => useContext(AgentControlContext);

/** Page controls and the agent panel share commands within this provider. */
export function AgentControlProvider({
  agentAvailable,
  children,
}: PropsWithChildren<{ agentAvailable: boolean }>) {
  const nudgeListeners = useRef(new Set<NudgeListener>());
  const loadSessionListeners = useRef(new Set<LoadSessionListener>());
  const startNewChatListeners = useRef(new Set<StartNewChatListener>());
  const openListeners = useRef(new Set<OpenListener>());
  const fireOpenListeners = useCallback(() => {
    for (const listener of openListeners.current) listener();
  }, []);
  const nudgeAgent = useCallback(
    (prompt: string, metadata?: AgentNudgeMetadata) => {
      fireOpenListeners();
      for (const listener of nudgeListeners.current) listener(prompt, metadata);
    },
    [fireOpenListeners],
  );
  const loadSession = useCallback(
    (sessionId: string) => {
      fireOpenListeners();
      for (const listener of loadSessionListeners.current) listener(sessionId);
    },
    [fireOpenListeners],
  );
  const startNewChat = useCallback(() => {
    fireOpenListeners();
    for (const listener of startNewChatListeners.current) listener();
  }, [fireOpenListeners]);
  const registerNudgeListener = useCallback((listener: NudgeListener) => {
    nudgeListeners.current.add(listener);
    return () => {
      nudgeListeners.current.delete(listener);
    };
  }, []);
  const registerLoadSessionListener = useCallback(
    (listener: LoadSessionListener) => {
      loadSessionListeners.current.add(listener);
      return () => {
        loadSessionListeners.current.delete(listener);
      };
    },
    [],
  );
  const registerStartNewChatListener = useCallback(
    (listener: StartNewChatListener) => {
      startNewChatListeners.current.add(listener);
      return () => {
        startNewChatListeners.current.delete(listener);
      };
    },
    [],
  );
  const registerOpenListener = useCallback((listener: OpenListener) => {
    openListeners.current.add(listener);
    return () => {
      openListeners.current.delete(listener);
    };
  }, []);
  const value = useMemo(
    () => ({
      agentAvailable,
      nudgeAgent,
      loadSession,
      startNewChat,
      registerNudgeListener,
      registerLoadSessionListener,
      registerStartNewChatListener,
      registerOpenListener,
    }),
    [
      agentAvailable,
      nudgeAgent,
      loadSession,
      startNewChat,
      registerNudgeListener,
      registerLoadSessionListener,
      registerStartNewChatListener,
      registerOpenListener,
    ],
  );
  return (
    <AgentControlContext.Provider value={value}>
      {children}
    </AgentControlContext.Provider>
  );
}
